/**
 * Research panel — centered modal listing the tech tree by echelon, the
 * active research progress, and the echelon advancement section with its
 * requirement checklist. Toggled by the "Research (T)" button or the T key.
 */
import type { GameDB } from "../data/gamedb";
import type { TechDef } from "../data/types";
import type { Economy } from "../sim/economy";
import { iconImg, techIcon } from "./icons";
import {
  canResearch,
  effectSummary,
  startResearch,
  type ResearchState,
} from "../sim/research";
import {
  advanceCost,
  canAdvance,
  doAdvance,
  echelonDef,
  type AdvanceStats,
  type EchelonState,
} from "../sim/echelon";

function costLabel(cost: Record<string, number>): string {
  const parts = Object.entries(cost).map(([k, v]) => `${v} ${k}`);
  return parts.length > 0 ? parts.join(", ") : "free";
}

export interface ResearchPanelContext {
  db: GameDB;
  economy: Economy;
  research: ResearchState;
  echelon: EchelonState;
  activeBuildingIds: () => Set<string>;
  activeMilitaryIds: () => Set<string>;
  advanceStats: () => AdvanceStats;
  onEchelonAdvanced: (name: string) => void;
  onTechResearched: (tech: TechDef) => void;
}

export class ResearchPanel {
  private el: HTMLDivElement;
  private toggleBtn: HTMLButtonElement;
  private open = false;
  private ctx: ResearchPanelContext | null = null;

  constructor() {
    this.toggleBtn = document.createElement("button");
    this.toggleBtn.textContent = "Research (T)";
    this.toggleBtn.style.cssText =
      "position:fixed;right:12px;bottom:64px;z-index:30;padding:8px 14px;" +
      "background:rgba(10,10,12,0.85);color:#e8e2d4;border:1px solid #3a2f22;" +
      "border-radius:8px;cursor:pointer;font-size:13px;";
    this.toggleBtn.addEventListener("click", () => this.toggle());
    document.body.appendChild(this.toggleBtn);

    this.el = document.createElement("div");
    this.el.style.cssText =
      "position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:40;" +
      "width:min(560px,92vw);max-height:82vh;overflow:auto;display:none;" +
      "background:rgba(8,8,10,0.96);border:1px solid #3a2f22;border-radius:10px;" +
      "padding:16px;font-size:13px;color:#e8e2d4;";
    document.body.appendChild(this.el);

    window.addEventListener("keydown", (e) => {
      if (e.key === "t" || e.key === "T") {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        this.toggle();
      }
    });
  }

  bind(ctx: ResearchPanelContext): void {
    this.ctx = ctx;
  }

  toggle(): void {
    this.open = !this.open;
    this.render();
  }

  render(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    this.el.style.display = this.open ? "block" : "none";
    if (!this.open) return;
    const { db, economy, research, echelon } = ctx;
    const buildingIds = ctx.activeBuildingIds();

    // Echelon section.
    const next = echelonDef(db, echelon.index + 1);
    let echelonHtml = "";
    if (next) {
      const cost = advanceCost(db, echelon);
      const check = canAdvance(
        db, echelon, research, buildingIds, ctx.activeMilitaryIds(), ctx.advanceStats(),
      );
      const reqList = check.missing.length > 0
        ? check.missing.map((m) => `<li style="color:#c96a5a;">✗ ${m}</li>`).join("")
        : `<li style="color:#7fae6a;">✓ all requirements met</li>`;
      const afford = economy.canAfford(cost);
      echelonHtml =
        `<div style="border:1px solid #3a2f22;border-radius:8px;padding:10px;margin-bottom:12px;">` +
        `<b>Advance to ${next.name_en}</b><br/>` +
        `<span style="color:#9a917f;">Cost: ${costLabel(cost)}</span>` +
        `<ul style="margin:6px 0;padding-left:18px;">${reqList}</ul>` +
        `<button id="advance-btn" ${check.ok && afford ? "" : "disabled"} ` +
        `style="padding:6px 12px;background:#2a2118;color:#e8e2d4;border:1px solid #b98b3a;` +
        `border-radius:5px;cursor:${check.ok && afford ? "pointer" : "not-allowed"};` +
        `opacity:${check.ok && afford ? 1 : 0.45};">Advance Echelon</button></div>`;
    } else {
      echelonHtml = `<div style="color:#9a917f;margin-bottom:12px;">Highest echelon reached.</div>`;
    }

    // Active research.
    const active = research.active
      ? (() => {
        const tech = db.technologies.get(research.active!.techId);
        const pct = Math.round((1 - research.active!.remaining / research.active!.total) * 100);
        return `<div style="margin-bottom:12px;color:#e8e2d4;">Researching <b>${tech?.name_en ?? research.active!.techId}</b> — ${pct}%` +
          `<div style="height:5px;background:#2a2d33;border-radius:2px;margin-top:3px;">` +
          `<div style="height:100%;width:${pct}%;background:#b98b3a;border-radius:2px;"></div></div></div>`;
      })()
      : "";

    // Tech list: current echelon's techs, researched first, then available.
    const techs = [...db.technologies.values()]
      .filter((t) => t.echelon <= echelon.index)
      .sort((a, b) => {
        const ra = research.completed.has(a.id) ? 0 : 1;
        const rb = research.completed.has(b.id) ? 0 : 1;
        return ra - rb || a.echelon - b.echelon || a.name_en.localeCompare(b.name_en);
      })
      .slice(0, 60);
    const rows = techs.map((t) => {
      if (research.completed.has(t.id)) {
        return `<div style="padding:5px 0;border-bottom:1px solid #1e2024;color:#7fae6a;">✓ ${iconImg(techIcon(t.id), t.name_en, 18)}${t.name_en}</div>`;
      }
      const { ok, reason } = canResearch(t, research, buildingIds, echelon.index);
      const afford = economy.canAfford(t.cost);
      const enabled = ok && afford;
      return `<div style="padding:6px 0;border-bottom:1px solid #1e2024;">` +
        `<b>${iconImg(techIcon(t.id), t.name_en, 18)}${t.name_en}</b> <span style="color:#9a917f;">${costLabel(t.cost)} · ${t.research_time}s</span><br/>` +
        `<span style="color:#9a917f;font-size:12px;">${effectSummary(t)}</span><br/>` +
        `<button data-tech="${t.id}" ${enabled ? "" : "disabled"} ` +
        `title="${enabled ? (t.extra["description"] as string ?? "") : reason || "Insufficient resources"}" ` +
        `style="margin-top:3px;padding:4px 10px;background:#1a1c20;color:#e8e2d4;` +
        `border:1px solid #3a2f22;border-radius:4px;cursor:${enabled ? "pointer" : "not-allowed"};` +
        `opacity:${enabled ? 1 : 0.45};">${ok ? "Research" : reason}</button></div>`;
    }).join("");

    const cur = echelonDef(db, echelon.index);
    this.el.innerHTML =
      `<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">` +
      `<b style="font-size:15px;">${cur?.name_en ?? `Echelon ${echelon.index}`} — Research</b>` +
      `<button id="close-btn" style="background:none;border:none;color:#9a917f;cursor:pointer;font-size:16px;">✕</button></div>` +
      echelonHtml + active +
      `<div style="color:#9a917f;margin:4px 0;">Technologies (showing up to 60)</div>` + rows;

    this.el.querySelector("#close-btn")?.addEventListener("click", () => this.toggle());
    this.el.querySelector("#advance-btn")?.addEventListener("click", () => {
      if (doAdvance(db, echelon, economy)) {
        const def = echelonDef(db, echelon.index);
        ctx.onEchelonAdvanced(def?.name_en ?? `Echelon ${echelon.index}`);
        this.render();
      }
    });
    this.el.querySelectorAll("button[data-tech]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const tech = db.technologies.get(btn.getAttribute("data-tech")!);
        if (!tech) return;
        if (startResearch(research, tech, economy)) this.render();
      });
    });
  }
}
